import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout1 from "../../Layouts/Layout1";
import LayoutContainer from "../../Layouts/LayoutContainer";
import { ChevronLeft, HelpCircle, Upload, X } from "lucide-react";
import RentalDataUseModal from "./RentalDataUseModal";
import { toast } from "react-toastify";
import { CustomSelect } from "../CustomSelection";
import { useRentalProfile } from "../../context/RentalProfileContext";
import { RENTAL_PROFILE_SECTIONS } from "../../Constants/rentalProfile";
import RentalFilePreview from "./RentalFilePreview";

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ACCEPT =
  "image/gif,image/jpeg,image/jpg,image/png,image/heic,image/heif,application/pdf,.pdf";

const FREQUENCY_OPTS = [
  { value: "", label: "Frequency" },
  { value: "weekly", label: "Weekly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "monthly", label: "Monthly" },
  { value: "annually", label: "Annually" },
];

const inputClass =
  "w-full rounded-lg border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#1A1A1A] outline-none transition-shadow placeholder:text-[#9CA3AF] focus:border-[#097FE6] focus:ring-2 focus:ring-[#097FE6]/20";

const newIncomeSource = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  incomeType: "",
  amount: "",
  frequency: "",
});

const Finances = () => {
  const navigate = useNavigate();
  const { profile, loading, saving, updateSection, uploadDocuments } =
    useRentalProfile();
  const fileInputRef = useRef(null);
  const [dataUseModalOpen, setDataUseModalOpen] = useState(false);
  const [noIncome, setNoIncome] = useState(false);
  const [incomeSources, setIncomeSources] = useState([]);
  const [savedProofDocuments, setSavedProofDocuments] = useState([]);
  const [pendingProofFiles, setPendingProofFiles] = useState([]);

  useEffect(() => {
    if (loading) return;
    const f = profile.finances;
    setNoIncome(Boolean(f.noIncome));
    setIncomeSources(
      f.incomeSources?.length
        ? f.incomeSources.map((src) => ({ ...newIncomeSource(), ...src }))
        : []
    );
    setSavedProofDocuments(f.proofDocuments || []);
    setPendingProofFiles([]);
  }, [loading, profile.finances]);

  const addIncomeSource = () => {
    if (noIncome) return;
    setIncomeSources((s) => [...s, newIncomeSource()]);
  };

  const removeIncomeSource = (id) => {
    setIncomeSources((s) => s.filter((x) => x.id !== id));
  };

  const updateIncomeSource = (id, key, value) => {
    setIncomeSources((sources) =>
      sources.map((src) => (src.id === id ? { ...src, [key]: value } : src))
    );
  };

  const handleNoIncomeChange = (e) => {
    const checked = e.target.checked;
    setNoIncome(checked);
    if (checked) setIncomeSources([]);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const next = [...pendingProofFiles];
    for (const f of files) {
      if (f.size > MAX_FILE_BYTES) {
        toast.error(`${f.name} must be 10MB or smaller.`);
        continue;
      }
      next.push(f);
    }
    setPendingProofFiles(next);
  };

  const removePendingFile = (index) => {
    setPendingProofFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let proofDocuments = [...savedProofDocuments];

    if (pendingProofFiles.length) {
      const uploaded = await uploadDocuments(pendingProofFiles);
      if (!uploaded.length) return;
      proofDocuments = [...proofDocuments, ...uploaded];
    }

    const ok = await updateSection(RENTAL_PROFILE_SECTIONS.FINANCES, {
      noIncome,
      incomeSources: incomeSources.map((src) => ({
        id: src.id,
        incomeType: src.incomeType?.trim() || "",
        amount: src.amount === "" ? "" : Number(src.amount),
        frequency: src.frequency || "",
      })),
      proofDocuments,
    });
    if (ok) navigate("/rental-applications");
  };

  return (
    <Layout1>
      <LayoutContainer className="max-w-xl py-8 md:py-12 lg:py-14">
        <button
          type="button"
          onClick={() => navigate("/rental-applications")}
          className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-[#097FE6] hover:underline"
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={2} />
          Back
        </button>

        <h1 className="text-2xl font-bold tracking-tight text-[#000929] md:text-3xl">
          Finances
        </h1>

        <div className="mt-8 rounded-xl border border-[#E5E7EB] bg-white p-6 shadow-sm md:p-8">
          <form onSubmit={handleSubmit} className="space-y-10">
            <section className="space-y-4">
              <h2 className="text-base font-semibold text-[#000929]">
                Sources of income
              </h2>
              <p className="text-sm leading-relaxed text-[#6B7280]">
                List all your income sources, such as wages, support payments,
                pensions and dividends.
              </p>
              <button
                type="button"
                onClick={addIncomeSource}
                disabled={noIncome}
                className="w-full rounded-lg border border-[#E5E7EB] bg-white py-3 text-sm font-semibold text-[#000929] transition-colors hover:bg-[#F9FAFB] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Add income source
              </button>
              {incomeSources.length > 0 && (
                <ul className="space-y-4">
                  {incomeSources.map((src, i) => (
                    <li
                      key={src.id}
                      className="space-y-3 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-4"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm font-semibold text-[#000929]">
                          Income source {i + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeIncomeSource(src.id)}
                          className="rounded p-1 text-[#6B7280] hover:bg-[#E5E7EB] hover:text-[#1A1A1A]"
                          aria-label="Remove"
                        >
                          <X className="h-4 w-4" strokeWidth={2} />
                        </button>
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-medium text-[#374151]">
                          Income type
                        </label>
                        <input
                          type="text"
                          value={src.incomeType}
                          onChange={(e) =>
                            updateIncomeSource(src.id, "incomeType", e.target.value)
                          }
                          placeholder="e.g. Salary, pension, dividends"
                          className={inputClass}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="mb-1.5 block text-xs font-medium text-[#374151]">
                            Amount
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={src.amount}
                            onChange={(e) =>
                              updateIncomeSource(src.id, "amount", e.target.value)
                            }
                            className={inputClass}
                          />
                        </div>
                        <div>
                          <label className="mb-1.5 block text-xs font-medium text-[#374151]">
                            Frequency
                          </label>
                          <CustomSelect
                            value={src.frequency}
                            onChange={(v) =>
                              updateIncomeSource(src.id, "frequency", v)
                            }
                            options={FREQUENCY_OPTS}
                            placeholder="Frequency"
                            usePortal={false}
                          />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <label className="flex cursor-pointer items-start gap-3 text-sm text-[#374151]">
                <input
                  type="checkbox"
                  checked={noIncome}
                  onChange={handleNoIncomeChange}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-[#D1D5DB] text-[#097FE6] focus:ring-[#097FE6]"
                />
                <span>I currently don&apos;t receive any income</span>
              </label>
            </section>

            <section className="space-y-4">
              <h2 className="text-base font-semibold text-[#000929]">
                Proof you can pay rent
              </h2>
              <p className="text-sm leading-relaxed text-[#6B7280]">
                Attach three recent payslips or other supporting documents. If
                using bank statements, hide account numbers and any non-income
                transactions.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                multiple
                className="hidden"
                onChange={handleFileChange}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-[#E5E7EB] bg-white py-3 text-sm font-medium text-[#1A1A1A] transition-colors hover:bg-[#F9FAFB]"
              >
                <Upload className="h-5 w-5 text-[#097FE6]" strokeWidth={2} />
                Upload a file
              </button>
              {savedProofDocuments.length > 0 && (
                <div className="space-y-2">
                  {savedProofDocuments.map((doc, i) => (
                    <RentalFilePreview
                      key={doc.id || doc.url || `saved-${i}`}
                      saved={doc}
                      label={`Document ${i + 1}`}
                    />
                  ))}
                </div>
              )}
              {pendingProofFiles.length > 0 && (
                <div className="space-y-2">
                  {pendingProofFiles.map((f, i) => (
                    <RentalFilePreview
                      key={`${f.name}-${i}`}
                      file={f}
                      label="New upload"
                      onRemove={() => removePendingFile(i)}
                    />
                  ))}
                </div>
              )}
              <p className="text-xs leading-relaxed text-[#9CA3AF]">
                Max. 10MB - GIF, JPG, JPEG, PNG, HEIC, PDF
              </p>
            </section>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading || saving}
                className="w-full rounded-lg bg-[#097FE6] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#0868c4] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save and back"}
              </button>
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => navigate("/rental-applications")}
                  className="text-sm font-medium text-[#097FE6] hover:underline"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>

        <button
          type="button"
          onClick={() => setDataUseModalOpen(true)}
          className="mt-10 inline-flex items-center gap-2 text-sm text-[#6B7280] underline decoration-[#6B7280] underline-offset-2 hover:text-[#1A1A1A]"
        >
          <HelpCircle className="h-4 w-4 shrink-0" strokeWidth={2} />
          How this information is used
        </button>
      </LayoutContainer>

      <RentalDataUseModal
        variant="finances"
        open={dataUseModalOpen}
        onClose={() => setDataUseModalOpen(false)}
      />
    </Layout1>
  );
};

export default Finances;
